import { AlertTriangle, Clock, Info } from "lucide-react";
import type { RefObject } from "react";

import {
  ConsequenceList,
  ConsequenceRow,
} from "@/modules/dashboard/payout-batches/components/execute/ConsequenceList";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { AdjustDirection, AdjustInlineProblem } from "../../types/cardWallets.types";
import {
  ADJUST_CURRENCY,
  ADJUST_REFERENCE_MAX_LENGTH,
  balanceAfterAdjustment,
  shortWalletId,
  signedMovementValue,
} from "../../utils/cardWalletAdjust";

export interface AdjustBalanceReviewProps {
  headingRef: RefObject<HTMLHeadingElement | null>;
  direction: AdjustDirection;
  organizationName: string;
  walletId: string;
  balanceBefore: string;
  magnitude: string;
  reference: string;
  acknowledged: boolean;
  onAcknowledgedChange: (checked: boolean) => void;
  /** Set when the balance moved under the review; the figures below are already the new ones. */
  balanceMoved: boolean;
  /** A submit that never left this step — today only the throttle. */
  inlineProblem: AdjustInlineProblem | null;
  retrySeconds: number | null;
}

/**
 * Step 2 — the only gate this operation has.
 *
 * The endpoint declares no two-factor requirement, unlike every comparable card-money route, so the weight is
 * entirely here: the consequence spelled out in figures, what the client will and will not be told, and an
 * acknowledgement that names the real-world precondition only the operator can attest to.
 */
export function AdjustBalanceReview({
  headingRef,
  direction,
  organizationName,
  walletId,
  balanceBefore,
  magnitude,
  reference,
  acknowledged,
  onAcknowledgedChange,
  balanceMoved,
  inlineProblem,
  retrySeconds,
}: AdjustBalanceReviewProps) {
  const { t } = useTranslations("cardWallets");
  const isCredit = direction === "credit";
  const balanceAfter = balanceAfterAdjustment(balanceBefore, magnitude, direction);

  return (
    <div className="space-y-5">
      {inlineProblem?.kind === "rate_limited" && (
        <ColocaAlert
          variant="warning"
          icon={Clock}
          text={
            retrySeconds !== null && retrySeconds > 0
              ? `${t("error_rate_limited")} ${t("error_rate_limited_retry", { seconds: retrySeconds })}`
              : t("error_rate_limited")
          }
        />
      )}

      {/* `initialFocus` points here: without it the body opens scrolled past everything it asks the reader to
          review, landing on the acknowledgement (design-system rule 5). */}
      <h3 ref={headingRef} tabIndex={-1} className="text-lg font-semibold tracking-tight outline-none">
        {t(isCredit ? "review_heading_credit" : "review_heading_debit", { name: organizationName })}
      </h3>

      <ConsequenceList>
        <ConsequenceRow label={t("review_organization")} value={organizationName} />
        <ConsequenceRow
          label={t("review_wallet")}
          value={t("review_wallet_value", { short: shortWalletId(walletId) })}
        />
        <ConsequenceRow
          label={t("review_balance_before")}
          value={<ColocaAmountDisplay size="sm" value={balanceBefore} currency={ADJUST_CURRENCY} />}
        />
        <ConsequenceRow
          label={t("review_movement")}
          value={
            <ColocaAmountDisplay
              size="sm"
              signed
              value={signedMovementValue(magnitude, direction)}
              currency={ADJUST_CURRENCY}
              className={cn(isCredit ? "text-success" : "text-destructive")}
            />
          }
        />
        <ConsequenceRow
          emphasis
          label={t("review_balance_after")}
          value={
            balanceAfter === null ? (
              "—"
            ) : (
              <ColocaAmountDisplay size="lg" value={balanceAfter} currency={ADJUST_CURRENCY} />
            )
          }
        />
      </ConsequenceList>

      {balanceMoved && (
        <ColocaAlert
          variant="warning"
          icon={AlertTriangle}
          text={t("review_balance_moved", { balance: `${balanceBefore} ${ADJUST_CURRENCY}` })}
        />
      )}

      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-muted-foreground">{t("review_reference")}</span>
          <span className="shrink-0 font-mono text-xs text-muted-foreground">
            {reference.length} / {ADJUST_REFERENCE_MAX_LENGTH}
          </span>
        </div>
        <p className="break-all rounded-lg border border-border bg-muted/30 px-3.5 py-3 font-mono text-sm">
          {reference}
        </p>
      </div>

      <div className="space-y-2.5">
        <ColocaAlert variant="secondary" icon={Info} text={t("review_client_sees")} />
        <ColocaAlert
          variant={isCredit ? "secondary" : "warning"}
          icon={isCredit ? Info : AlertTriangle}
          text={t(isCredit ? "review_webhook_credit" : "review_webhook_debit")}
        />
        <ColocaAlert variant="warning" icon={AlertTriangle} text={t("review_irreversible")} />
      </div>

      <ColocaCheckbox
        checked={acknowledged}
        onCheckedChange={onAcknowledgedChange}
        label={t(isCredit ? "review_checkbox_credit" : "review_checkbox_debit")}
        className="rounded-xl border border-border p-3.5"
      />
    </div>
  );
}
